import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AREA_LEVEL_DESCRIPTIONS,
  AREA_LEVEL_LABELS,
  AREA_LEVELS,
  type AreaAccessRequest,
  type AreaLevel,
  type ProjectAreaResponse,
} from '@k-management/shared';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { useCan } from '../../auth/AuthProvider.js';
import { usePageTitle } from '../../components/layout/page-chrome.js';
import { apiRequest } from '../../lib/api.js';

const areaKeys = {
  list: ['jobs', 'project-areas'] as const,
  users: ['jobs', 'project-areas', 'users'] as const,
};

/**
 * Operations › Progetti › Aree e accessi (2.41, ADR-025): area by area, the
 * users who see, change or administer its progetti, also without the module.
 * The areas themselves are the entries of Tabelle › Aree dei progetti.
 */
export function ProjectAreasPage() {
  usePageTitle('Aree e accessi');
  const canTables = useCan('tabelle.read');
  const [editing, setEditing] = useState<ProjectAreaResponse | null>(null);
  const areas = useQuery({
    queryKey: areaKeys.list,
    queryFn: () => apiRequest<ProjectAreaResponse[]>('/project-areas'),
  });
  return (
    <section className="stack">
      <div className="page-header">
        <div>
          <p className="muted small">
            <Link to="/progetti">Progetti</Link> › Aree e accessi
          </p>
          <h1>Aree e accessi</h1>
          <p className="muted">
            Per ogni area scegli chi vede, chi modifica e chi amministra i suoi progetti, anche tra
            gli utenti senza il modulo Commesse e progetti. Il PM vede e aggiorna sempre il proprio
            progetto. Le aree si aggiungono in{' '}
            {canTables ? (
              <Link to="/tabelle/aree-progetti">Tabelle › Aree dei progetti</Link>
            ) : (
              'Tabelle › Aree dei progetti'
            )}
            .
          </p>
        </div>
      </div>
      {areas.isError && <p className="error">{areas.error.message}</p>}
      {areas.data?.length === 0 && (
        <p className="notice info">Nessuna area: aggiungile in Tabelle › Aree dei progetti.</p>
      )}
      {areas.data?.map((area) => (
        <section key={area.code} className="card stack">
          <div className="row between">
            <h2>
              {area.name} <span className="count-pill">{area.access.length}</span>
            </h2>
            <button type="button" onClick={() => setEditing(area)}>
              Chi accede…
            </button>
          </div>
          {area.access.length === 0 ? (
            <p className="muted">
              Nessun utente: i progetti dell’area li vede solo chi ha il modulo, e il loro PM.
            </p>
          ) : (
            <div className="table-scroll">
              <table className="lines">
                <thead>
                  <tr>
                    <th>Utente</th>
                    <th>Accesso</th>
                    <th>Cosa può fare</th>
                  </tr>
                </thead>
                <tbody>
                  {area.access.map((item) => (
                    <tr key={item.userId}>
                      <td>
                        <strong>{item.name}</strong> <small className="muted">{item.email}</small>
                      </td>
                      <td>
                        <span className={`pill ${item.level === 'read' ? 'muted' : 'ok'}`}>
                          {AREA_LEVEL_LABELS[item.level]}
                        </span>
                      </td>
                      <td className="muted">{AREA_LEVEL_DESCRIPTIONS[item.level]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      ))}
      {editing && <AccessDialog area={editing} onClose={() => setEditing(null)} />}
    </section>
  );
}

function AccessDialog({ area, onClose }: { area: ProjectAreaResponse; onClose: () => void }) {
  const queryClient = useQueryClient();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [rows, setRows] = useState<{ userId: string; level: AreaLevel }[]>(
    area.access.map(({ userId, level }) => ({ userId, level })),
  );
  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);
  const users = useQuery({
    queryKey: areaKeys.users,
    queryFn: () =>
      apiRequest<{ id: string; name: string; email: string }[]>('/project-areas/users'),
  });
  const save = useMutation({
    mutationFn: (body: AreaAccessRequest) =>
      apiRequest<ProjectAreaResponse[]>(`/project-areas/${encodeURIComponent(area.code)}`, {
        method: 'PUT',
        body,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['jobs'] });
      onClose();
    },
  });
  const update = (index: number, change: Partial<(typeof rows)[number]>) =>
    setRows(rows.map((row, at) => (at === index ? { ...row, ...change } : row)));
  const chosen = new Set(rows.map((row) => row.userId));
  const complete = rows.every((row) => row.userId);
  return (
    <dialog ref={dialogRef} className="dialog" onClose={onClose}>
      <div className="stack">
        <h2>{area.name} · chi accede</h2>
        <ul className="plain-list small muted">
          {AREA_LEVELS.map((level) => (
            <li key={level}>
              <strong>{AREA_LEVEL_LABELS[level]}</strong>: {AREA_LEVEL_DESCRIPTIONS[level]}
            </li>
          ))}
        </ul>
        {rows.map((row, index) => (
          <div key={index} className="row">
            <select
              style={{ flex: 1, width: 'auto' }}
              value={row.userId}
              onChange={(event) => update(index, { userId: event.target.value })}
              aria-label="Utente"
            >
              <option value="">Scegli…</option>
              {users.data
                ?.filter((user) => user.id === row.userId || !chosen.has(user.id))
                .map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name}
                  </option>
                ))}
            </select>
            <select
              style={{ width: 'auto' }}
              value={row.level}
              onChange={(event) => update(index, { level: event.target.value as AreaLevel })}
              aria-label="Accesso"
            >
              {AREA_LEVELS.map((level) => (
                <option key={level} value={level}>
                  {AREA_LEVEL_LABELS[level]}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="link"
              onClick={() => setRows(rows.filter((_, at) => at !== index))}
            >
              Togli
            </button>
          </div>
        ))}
        <button
          type="button"
          className="link"
          onClick={() => setRows([...rows, { userId: '', level: 'read' }])}
        >
          + Aggiungi utente
        </button>
        {save.isError && <p className="error">{save.error.message}</p>}
        <div className="actions">
          <button type="button" onClick={onClose} disabled={save.isPending}>
            Annulla
          </button>
          <button
            type="button"
            className="primary"
            disabled={!complete || save.isPending}
            onClick={() => save.mutate({ entries: rows })}
          >
            {save.isPending ? 'Attendere…' : 'Salva'}
          </button>
        </div>
      </div>
    </dialog>
  );
}
